import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { RefreshCw, Clock, Users, Filter } from 'lucide-react'
import type { RadarBubble, RadarZones, RadarRay, RadarConfigResponse, RadarFilterState } from '@/services/api/teacher'
import { DEFAULT_MAX_TIME_RANGE_DAYS, DEFAULT_TIME_RANGE_DAYS, getAdminCollapsedTeachers, setAdminCollapsedTeachers, pruneCollapsedTeachers } from '@/utils/radarStorage'
import { useAuthStore } from '@/store'
import { isAdminCollapsedHidden } from './canvas/BubbleRenderer'

// Glass Components
import { GlassContainer } from './glass-layers/GlassContainer'
import { GlassZones } from './glass-layers/GlassZones'
import { OrganicRays } from './svg/OrganicRays'
import { RadarCanvas } from './canvas/RadarCanvas'
import { RadarErrorBoundary } from './ErrorBoundary'
import { RequestSelectionDialog } from './ui/RequestSelectionDialog'
import { HoveredBubbleInfo } from './ui/HoveredBubbleInfo'
import { FloatingTooltip } from './ui/FloatingTooltip'
import { RadarFilterDrawer } from './ui/RadarFilterDrawer'
import { RadarBadgeRing, type BadgeRingEntry } from './ui/RadarBadgeRing'
import { filterBubbles, countActiveFilters, filterRays, bucketForBubble, withoutDirectorSpecials, stripDirectorGate } from './utils/filterBubbles'

// Hooks
import { useRadarSize, useTouchDevice } from './hooks/useRadarSize'
import { useRadarFocus } from './hooks/useRadarFocus'
import { useInView } from './hooks/useInView'

// i18n
import {
  getTranslations,
  detectLocale,
  formatDays,
  type Locale,
} from './i18n'

// Import CSS tokens
import './styles/radar-tokens.css'

const EMPTY_COLLAPSED: string[] = []

interface AdminRadarProps {
  bubbles: RadarBubble[]
  zones: RadarZones | undefined
  rays: RadarRay[]
  timeRange: { days: number; from: string; to: string } | undefined
  config: RadarConfigResponse | null
  isLoading: boolean
  isConfigLoading: boolean
  onBubbleClick: (bubble: RadarBubble) => void
  onTimeRangeChange: (days: number) => void
  onRefresh: () => void
  locale?: Locale
  // Filter (filter-fix-v2) — optional for backward compat / tests
  filterState?: RadarFilterState
  onFilterChange?: (partial: Partial<RadarFilterState>) => void
  onResetFilter?: () => void
  isFilterLocked?: boolean
  filterDefaultState?: RadarFilterState
  directorEnabled?: boolean
}

/**
 * Admin Radar - Glass Edition v2
 *
 * Phase 2: Accessibility & i18n
 * - Keyboard navigation (Tab, Arrows, Enter, Escape)
 * - Screen reader support
 * - IntersectionObserver for performance
 * - ARIA attributes
 * - Full i18n support
 */
export function AdminRadar({
  bubbles,
  zones,
  rays,
  timeRange,
  config,
  isLoading,
  isConfigLoading,
  onBubbleClick,
  onTimeRangeChange,
  onRefresh,
  locale: localeProp,
  filterState,
  onFilterChange,
  onResetFilter,
  isFilterLocked,
  filterDefaultState,
  directorEnabled = false,
}: AdminRadarProps) {
  const locale = localeProp ?? detectLocale()
  const t = getTranslations(locale)

  const displayDays = timeRange?.days ?? config?.default_time_range_days ?? DEFAULT_TIME_RANGE_DAYS
  const [localTimeRange, setLocalTimeRange] = useState(displayDays)
  const [hoveredBubble, setHoveredBubble] = useState<RadarBubble | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const [selectedBubble, setSelectedBubble] = useState<RadarBubble | null>(null)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const [tooltipState, setTooltipState] = useState<{
    visible: boolean
    position: { x: number; y: number }
  }>({
    visible: false,
    position: { x: 0, y: 0 },
  })

  const [tappedBubble, setTappedBubble] = useState<RadarBubble | null>(null)

  // Responsive sizing
  const { size: radarSize, isMobile } = useRadarSize({
    mobile: 300,
    tablet: 450,
    desktop: 600,
    large: 700,
  })
  const isTouchDevice = useTouchDevice()

  // Refs
  const radarContainerRef = useRef<HTMLDivElement>(null)
  const radarSectionRef = useRef<HTMLElement>(null)

  // Check if radar is in view (for performance)
  const isInView = useInView(radarSectionRef, { threshold: 0.1 })

  // Keyboard navigation
  const handleBubbleSelect = useCallback((bubble: RadarBubble | null) => {
    setHoveredBubble(bubble)
    if (bubble) {
      setAnnouncement(`${bubble.requester_name} — ${bubble.subject_name ?? ''}`)
    }
  }, [])

  const handleBubbleActivate = useCallback((bubble: RadarBubble) => {
    onBubbleClick(bubble)
  }, [onBubbleClick])

  // ==========================================================================
  // Filter + Kollaps (bubblenew-v2/v3) — VOR dem Fokus-Hook, damit die
  // Keyboard-Navigation nur über SICHTBARE Bubbles läuft (B1-Fix).
  // ==========================================================================

  // Filter bubbles client-side (filter-fix-v2). When no filterState is provided,
  // filtering is disabled (backward compat) and all bubbles render.
  // bubblenew-v3 (C1): memoisiert — stabilisiert fanLayout/badgeEntries-Deps.
  const effectiveFilter = useMemo(() => {
    if (!filterState) return filterState
    if (directorEnabled) return filterState
    return withoutDirectorSpecials(filterState)
  }, [filterState, directorEnabled])
  const sourceBubbles = directorEnabled ? bubbles : stripDirectorGate(bubbles)
  const filteredBubbles = useMemo(
    () => (effectiveFilter ? filterBubbles(sourceBubbles, effectiveFilter, { applyTypeFilter: false }) : sourceBubbles),
    [sourceBubbles, effectiveFilter]
  )
  const isFiltering = !!filterState && filteredBubbles.length !== bubbles.length

  // bubblenew-v2 (E3): Kollabiert-Set + Persistenz, user-scoped.
  // bubblenew-v3 (B2): tote IDs prunen — nicht bei leerer Liste (erster Paint
  // vor dem Fetch würde sonst den Storage leeren).
  const userId = useAuthStore(s => s.user?.id)
  const [collapsedTeachers, setCollapsedTeachers] = useState<string[]>(() =>
    getAdminCollapsedTeachers(useAuthStore.getState().user?.id)
  )
  useEffect(() => {
    setCollapsedTeachers(getAdminCollapsedTeachers(userId))
  }, [userId])
  useEffect(() => {
    setAdminCollapsedTeachers(collapsedTeachers, userId)
  }, [collapsedTeachers, userId])
  useEffect(() => {
    const active = bubbles
      .map(b => b.requester_teacher_id)
      .filter((id): id is string => !!id)
    setCollapsedTeachers(prev => pruneCollapsedTeachers(prev, active))
  }, [bubbles])

  const badgeRingEnabled = config?.admin_badge_ring_enabled ?? true
  const panicThreshold = zones?.panic_zone.threshold_hours ?? 2
  const effectiveCollapsed = badgeRingEnabled ? collapsedTeachers : EMPTY_COLLAPSED
  const collapsedSet = useMemo(() => new Set(effectiveCollapsed), [effectiveCollapsed])

  // bubblenew-v3 (B1): Sichtbarkeitsregel identisch zu RadarCanvas — kollabierte
  // Lehrer verbergen alle Bubbles ausser Panik (innerhalb des aktiven Filters).
  const focusBubbles = useMemo(
    () => filteredBubbles.filter(b => !isAdminCollapsedHidden(b, collapsedSet, panicThreshold)),
    [filteredBubbles, collapsedSet, panicThreshold]
  )

  const minBubbleSize = config?.min_bubble_size
  const maxBubbleSize = config?.max_bubble_size
  const bubbleSizeScale = config?.bubble_size_scale
  const dateFanEnabled = config?.admin_date_fan_enabled
  const fanOverlapFallback = config?.admin_fan_overlap_fallback
  const aggregationEnabled = config?.enable_bubble_aggregation
  const canvasConfig = useMemo(
    () => minBubbleSize == null || maxBubbleSize == null ? null : {
      min_bubble_size: minBubbleSize,
      max_bubble_size: maxBubbleSize,
      bubble_size_scale: bubbleSizeScale,
      admin_date_fan_enabled: dateFanEnabled,
      admin_fan_overlap_fallback: fanOverlapFallback,
      enable_bubble_aggregation: aggregationEnabled,
    },
    [minBubbleSize, maxBubbleSize, bubbleSizeScale, dateFanEnabled, fanOverlapFallback, aggregationEnabled]
  )

  const handleBadgeToggle = useCallback((teacherId: string) => {
    setCollapsedTeachers(prev =>
      prev.includes(teacherId)
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    )
  }, [])

  const badgeEntries = useMemo<BadgeRingEntry[]>(() => {
    const byTeacher = new Map<string, {
      code: string
      name: string
      angle: number
      lessonCount: number
      dates: Set<string>
    }>()
    for (const b of filteredBubbles) {
      if (!b.requester_teacher_id) continue
      const entry = byTeacher.get(b.requester_teacher_id) ?? {
        code: b.requester_short_code || '???',
        name: b.requester_name || b.requester_short_code || 'Unbekannt',
        angle: b.angle_degrees,
        lessonCount: 0,
        dates: new Set<string>(),
      }
      entry.lessonCount += b.lesson_count || 1
      if (b.lesson_date) entry.dates.add(b.lesson_date)
      byTeacher.set(b.requester_teacher_id, entry)
    }
    return Array.from(byTeacher.entries()).map(([teacherId, e]) => ({
      teacherId,
      code: e.code,
      name: e.name,
      lessonCount: e.lessonCount,
      dateCount: e.dates.size,
      angleDeg: e.angle,
      collapsed: collapsedTeachers.includes(teacherId),
    }))
  }, [filteredBubbles, collapsedTeachers])

  const {
    focusedId,
    setFocusedId,
    focusedBubble,
    handleKeyDown,
  } = useRadarFocus({
    bubbles: focusBubbles,
    onBubbleSelect: handleBubbleSelect,
    onBubbleActivate: handleBubbleActivate,
  })

  // Sync focused bubble with hovered bubble
  useEffect(() => {
    if (focusedBubble) {
      setHoveredBubble(focusedBubble)
    }
  }, [focusedBubble])

  const visibleIdKey = focusBubbles.map(b => b.id).join('|')
  useEffect(() => {
    const visible = new Set(visibleIdKey ? visibleIdKey.split('|') : [])
    setHoveredBubble(prev => {
      if (prev && !visible.has(prev.id)) {
        setTooltipState(t => ({ ...t, visible: false }))
        return null
      }
      return prev
    })
    setTappedBubble(prev => (prev && !visible.has(prev.id) ? null : prev))
  }, [visibleIdKey])

  useEffect(() => {
    setLocalTimeRange(displayDays)
  }, [displayDays])

  const handleSliderChange = (days: number) => {
    const min = config?.min_time_range_days || 1
    const max = config?.max_time_range_days || DEFAULT_MAX_TIME_RANGE_DAYS
    const clamped = Math.max(min, Math.min(max, days))
    setLocalTimeRange(clamped)
    onTimeRangeChange(clamped)
  }

  const handleBubbleClickFn = useCallback((bubble: RadarBubble) => {
    if (isMobile || isTouchDevice) {
      setTappedBubble(bubble)
    }

    if (bubble.request_ids && bubble.request_ids.length > 1) {
      setSelectedBubble(bubble)
    } else {
      onBubbleClick(bubble)
    }
  }, [isMobile, isTouchDevice, onBubbleClick])

  const handleRequestSelect = (requestId: string, lessonRequestIds: string[]) => {
    if (selectedBubble) {
      onBubbleClick({ ...selectedBubble, request_id: requestId, lesson_request_ids: lessonRequestIds })
      setSelectedBubble(null)
    }
  }

  const handleBubbleHover = useCallback((bubble: RadarBubble | null, mousePos?: { x: number; y: number }) => {
    setHoveredBubble(bubble)
    setFocusedId(bubble?.id || null)

    if (bubble && mousePos && !isMobile) {
      setTooltipState({
        visible: true,
        position: mousePos,
      })
    } else {
      setTooltipState(prev => ({ ...prev, visible: false }))
    }
  }, [isMobile, setFocusedId])

  // Wrap handleKeyDown to convert React event to native event
  const handleSectionKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    handleKeyDown(e.nativeEvent)
  }, [handleKeyDown])

  // Filter circumference rays to match visible bubbles — ONLY when filtering is
  // active, so the unfiltered view is byte-identical to before (filter-fix-v9).
  const filteredRays = isFiltering ? filterRays(rays, filteredBubbles) : rays
  const activeFilterCount = filterState && filterDefaultState
    ? countActiveFilters(effectiveFilter ?? filterState, filterDefaultState)
    : 0

  // Count unique teachers with requests
  const uniqueTeachers = new Set(filteredBubbles.map(b => b.requester_teacher_id)).size
  const panicBubbles = filteredBubbles.filter(b => b.is_in_panic_zone).length
  const pendingBubbles = filteredBubbles.filter(b => bucketForBubble(b) === 'pending').length
  const resolvedBubbles = filteredBubbles.filter(b => bucketForBubble(b) === 'filled').length
  const declinedBubbles = filteredBubbles.filter(b => bucketForBubble(b) === 'declined').length

  // Default zones if not provided
  const defaultZones: RadarZones = {
    panic_zone: { threshold_hours: 2, type: 'fixed', color: '#ef4444', opacity: 0.2, label: 'PANIKZONE' },
    middle_zone: { threshold_hours: 48, type: 'fixed', color: '#f97316', opacity: 0.15, label: 'MITTELZONE' },
    outer_zone: { threshold_hours: null, type: 'auto', color: '#94a3b8', opacity: 0.1, label: 'AUßENZONE' },
  }

  const activeZones = zones || defaultZones

  return (
    <section
      ref={radarSectionRef}
      aria-label={t.aria.regionLabel}
      role="region"
      onKeyDown={handleSectionKeyDown}
    >
      {/* Screen reader instructions */}
      <div id="admin-radar-instructions" className="sr-only">
        {t.aria.instructions}
      </div>

      {/* Live region for announcements */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>

      <GlassContainer className="w-full relative">
        {/* Header */}
        <div className="p-4 border-b border-gray-200/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-800">{t.admin.title}</h3>
            {timeRange && (
              <span className="text-xs text-gray-500">
                ({formatDays(timeRange.days, locale)})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {bubbles.length > 0 && (
              <div className="flex items-center gap-2">
                <span
                  className="text-xs px-2 py-1 rounded-lg flex items-center gap-1"
                  style={{
                    background: 'rgba(255,255,255,0.5)',
                    border: '1px solid rgba(255,255,255,0.5)',
                  }}
                >
                  <Users className="w-3 h-3" />
                  {uniqueTeachers} {t.admin.teachers}
                </span>
                <span
                  className="text-xs px-2 py-1 rounded-lg"
                  style={{
                    background: 'rgba(255,255,255,0.5)',
                    border: '1px solid rgba(255,255,255,0.5)',
                  }}
                >
                  {isFiltering
                    ? `${filteredBubbles.length}/${bubbles.length} ${t.admin.requests}`
                    : `${bubbles.length} ${t.admin.requests}`}
                </span>
                {panicBubbles > 0 && (
                  <span
                    className="text-xs px-2 py-1 rounded-lg font-medium"
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#ef4444',
                    }}
                  >
                    {panicBubbles} {t.admin.panic}
                  </span>
                )}
              </div>
            )}
            {filterState && onFilterChange && (
              <button
                onClick={() => setFilterDrawerOpen(v => !v)}
                className={`relative p-2 rounded-lg hover:bg-white/50 transition border ${filterDrawerOpen ? 'bg-blue-50 border-blue-300' : 'border-gray-200/50'}`}
                title={t.filter.title}
                aria-label={t.filter.title}
              >
                <Filter className={`w-4 h-4 ${activeFilterCount > 0 ? 'text-blue-600' : 'text-gray-600'}`} />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 rounded-full bg-blue-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-white/50 transition disabled:opacity-50"
              title={t.buttons.refresh}
              aria-label={t.aria.refreshButton}
            >
              <RefreshCw className={`w-4 h-4 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="px-4 py-3 border-b border-gray-200/50 flex items-center gap-3">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500">{t.form.timeRange}:</span>
          <input
            type="range"
            min={config?.min_time_range_days || 1}
            max={config?.max_time_range_days || DEFAULT_MAX_TIME_RANGE_DAYS}
            value={localTimeRange}
            onChange={(e) => handleSliderChange(Number(e.target.value))}
            disabled={isConfigLoading || !!isFilterLocked}
            className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-blue-500 disabled:opacity-50"
            aria-label={t.aria.timeRangeSlider}
          />
          <span className="text-xs font-medium text-gray-700 w-8 text-right">{localTimeRange}{t.daysShort}</span>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <div className="text-center py-12" role="status" aria-live="polite">
              <div
                className="inline-block rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent animate-spin"
                style={{
                  boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)',
                }}
              />
              <p className="mt-3 text-sm text-gray-500">{t.loading.default}</p>
            </div>
          ) : bubbles.length === 0 && !isFiltering ? (
            <div
              className="text-center py-8 text-gray-500 text-sm rounded-xl border"
              style={{
                background: 'rgba(255,255,255,0.3)',
                borderColor: 'rgba(255,255,255,0.5)',
              }}
              role="status"
            >
              {t.empty.default}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Radar Visualization */}
              <div
                ref={radarContainerRef}
                className="relative flex justify-center"
                style={{ width: radarSize, height: radarSize, margin: '0 auto' }}
              >
                {/* Layer -2: Glass Zones */}
                <GlassZones
                  zones={activeZones}
                  width={radarSize}
                  height={radarSize}
                  timeRange={timeRange}
                  locale={locale}
                />

                {/* Layer -1: Organic Rays (bubblenew-v2: Labels aus, wenn der Badge-Ring sie ersetzt) */}
                <OrganicRays
                  rays={filteredRays}
                  width={radarSize}
                  height={radarSize}
                  hideLabels={badgeRingEnabled || config?.show_teacher_labels === false}
                />

                {/* Layer 0: Canvas (bubbles + center) */}
                <RadarErrorBoundary>
                  <RadarCanvas
                    bubbles={filteredBubbles}
                    zones={activeZones}
                    rays={filteredRays}
                    config={canvasConfig}
                    timeRange={timeRange}
                    width={radarSize}
                    height={radarSize}
                    onBubbleClick={handleBubbleClickFn}
                    onBubbleHover={handleBubbleHover}
                    containerRef={radarContainerRef}
                    isPaused={!isInView}
                    focusedId={focusedId}
                    radarType="admin"
                    collapsedTeacherIds={effectiveCollapsed}
                  />
                </RadarErrorBoundary>

                {/* Layer 1: Badge-Ring (bubblenew-v2) — ausserhalb der Disk,
                    ersetzt die Ray-Labels; Klick kollabiert/öffnet das Bündel */}
                {badgeRingEnabled && (
                  <RadarBadgeRing
                    entries={badgeEntries}
                    size={radarSize}
                    onToggle={handleBadgeToggle}
                    labels={{
                      collapse: t.admin.badgeCollapse,
                      expand: t.admin.badgeExpand,
                      lessonCount: t.admin.badgeLessonCount,
                    }}
                  />
                )}

                {/* 
                  Center "NOW" Label - pointer-events-none
                  Clicks on center hit the bubble at 0h (Canvas z-index: 2)
                */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: radarSize / 2,
                    top: radarSize / 2,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 50,
                  }}
                >
                  <div
                    className="px-3 py-1.5 rounded-full text-xs font-bold tracking-wide glass-chip"
                    style={{
                      color: '#1f2937',
                      boxShadow: '0 0 12px rgba(59, 130, 246, 0.25)',
                      background: 'rgba(255, 255, 255, 0.85)',
                    }}
                  >
                    {t.nowLabel}
                  </div>
                </div>
              </div>

              {isFiltering && filteredBubbles.length === 0 && (
                <div
                  className="text-center py-4 text-gray-500 text-sm rounded-xl border"
                  style={{
                    background: 'rgba(255,255,255,0.3)',
                    borderColor: 'rgba(255,255,255,0.5)',
                  }}
                  role="status"
                >
                  {t.filter.emptyFiltered}
                </div>
              )}

              {/* Mobile: Show tapped bubble info */}
              {(isMobile || isTouchDevice) && tappedBubble && (
                <HoveredBubbleInfo bubble={tappedBubble} isAdminView />
              )}

              {/* Desktop: Show hovered bubble info */}
              {!(isMobile || isTouchDevice) && hoveredBubble && (
                <HoveredBubbleInfo bubble={hoveredBubble} isAdminView />
              )}
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-gray-200/50">
            <div className="flex flex-wrap gap-4 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-orange-500" />
                <span>{t.admin.requestOpen}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span>{t.admin.filled}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-blue-500" />
                <span>{t.filter.statusInterested}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-gray-500" />
                <span>{t.filter.statusCancelled}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-gray-500" />
                <span>{t.filter.statusExpired}</span>
              </div>
              {/* frost-v2 (E12): Frost-Legende, nur wenn eine gepauste Anfrage sichtbar */}
              {filteredBubbles.some(b => b.gate_pending) && (
                <div className="flex items-center gap-1">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ background: '#e0f2fe', border: '2px solid #7dd3fc' }}
                  />
                  <span>{t.legend.gateFrozen}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span>{t.status.declined}</span>
              </div>
              {zones && (
                <>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border-2 border-red-500 bg-red-100" />
                    <span>{t.legend.panicZone} (≤{zones.panic_zone.threshold_hours}{t.legend.hoursShort})</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border-2 border-orange-500 bg-orange-100" />
                    <span>{t.legend.middleZone} ({zones.panic_zone.threshold_hours}-{zones.middle_zone.threshold_hours}{t.legend.hoursShort})</span>
                  </div>
                </>
              )}
            </div>
            {t.legend.splitHint && (
              <div className="mt-1 text-[11px] text-gray-400">{t.legend.splitHint}</div>
            )}

            {/* Summary stats */}
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="text-gray-500">{t.admin.summaryOpen}: <strong className="text-orange-600">{pendingBubbles}</strong></span>
              <span className="text-gray-500">{t.admin.summaryFilled}: <strong className="text-green-600">{resolvedBubbles}</strong></span>
              {declinedBubbles > 0 && (
                <span className="text-gray-500">{t.status.declined}: <strong className="text-red-600">{declinedBubbles}</strong></span>
              )}
              {panicBubbles > 0 && (
                <span className="text-red-600 font-medium">⚠ {t.admin.summaryPanic}: {panicBubbles}</span>
              )}
            </div>
          </div>
        </div>

        {/* Multi-request selection dialog */}
        <RequestSelectionDialog
          isOpen={!!selectedBubble}
          onClose={() => setSelectedBubble(null)}
          requestIds={selectedBubble?.request_ids || []}
          onSelect={handleRequestSelect}
          bubble={selectedBubble}
        />
      </GlassContainer>

      {/* Filter Drawer (filter-fix-v2/v3) — rendered OUTSIDE GlassContainer to
          avoid overflow-hidden clipping. Fixed-positioned (viewport-relative). */}
      {filterState && onFilterChange && (
        <RadarFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          filterState={filterState}
          setFilter={onFilterChange}
          resetFilter={onResetFilter ?? (() => {})}
          isLocked={!!isFilterLocked}
          radarType="admin"
          showIncoming={false}
          allBubbles={bubbles}
          totalCount={bubbles.length}
          visibleCount={filteredBubbles.length}
          t={t}
          directorEnabled={directorEnabled}
        />
      )}

      {/* Floating Tooltip - only on desktop */}
      {!isMobile && !isTouchDevice && (
        <FloatingTooltip
          bubble={hoveredBubble}
          position={tooltipState.position}
          visible={tooltipState.visible}
          isAdminView
        />
      )}
    </section>
  )
}

// Export old interface for backward compatibility (will be removed later)
export interface LegacyRadarBubble {
  id: string
  teacherName: string
  teacherCode: string
  status: 'open' | 'confirmed' | 'declined'
  subject: string
  periods: number
  timeToLesson: number
  hasMessages: boolean
}
